import type { ApiClientConfig, RequestOptions } from '@/types/api'
import { ApiError, NetworkError, TimeoutError } from '@/types/api'
import { CSRF_HEADER, CSRF_VALUE, currentPath, noteAuthenticated, redirectToLogin } from './session'

/** Options accepted by the low-level sender. */
interface SendOptions extends RequestOptions {
  /**
   * Request Content-Type. Defaults to `application/json`; pass `null` to send
   * no Content-Type at all (required for FormData, whose boundary the browser
   * must set itself).
   */
  contentType?: string | null
}

/** Pull the filename out of a Content-Disposition header, if it has one. */
function filenameFromDisposition(disposition: string | null): string | null {
  if (!disposition) return null
  const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8 && utf8[1]) {
    try {
      return decodeURIComponent(utf8[1])
    } catch {
      return utf8[1]
    }
  }
  const plain = disposition.match(/filename="?([^";]+)"?/i)
  return plain && plain[1] ? plain[1] : null
}

class ApiClient {
  private baseURL: string
  private timeout: number
  private bearerToken?: string

  constructor(config: ApiClientConfig) {
    // VITE_API_URL is the full origin (e.g. https://api.rivulet.webby.sigler.io).
    // In dev, leave it empty so requests go through the Vite proxy.
    this.baseURL = config.baseURL || import.meta.env.VITE_API_URL || ''
    this.timeout = config.timeout || 30000 // 30 seconds default

    // Bearer tokens are only used for CLI / programmatic API clients.
    // Browser sessions authenticate via the OIDC session cookie.
    this.bearerToken = import.meta.env.VITE_API_TOKEN || localStorage.getItem('api_token') || undefined
  }

  /**
   * Set Bearer token for API authentication (CLI / programmatic clients)
   */
  setBearerToken(token: string): void {
    this.bearerToken = token
    localStorage.setItem('api_token', token)
  }

  /**
   * Clear Bearer token
   */
  clearBearerToken(): void {
    this.bearerToken = undefined
    localStorage.removeItem('api_token')
  }

  /**
   * Build query string from params object
   */
  private buildQueryString(
    params?: Record<string, string | number | boolean | undefined>
  ): string {
    if (!params) return ''

    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value))
      }
    })

    const queryString = searchParams.toString()
    return queryString ? `?${queryString}` : ''
  }

  /**
   * Bearer token overrides cookie auth (for CLI / programmatic clients).
   * Otherwise the browser sends the OIDC session cookie automatically.
   * X-Requested-With is the CSRF header the API requires on every
   * cookie-authenticated state-changing request (authentication.md §7,
   * layer 3); sending it unconditionally is harmless in bearer mode.
   */
  private buildHeaders(extra?: HeadersInit, contentType?: string | null): Record<string, string> {
    const headers: Record<string, string> = {
      [CSRF_HEADER]: CSRF_VALUE,
    }
    if (contentType !== null) {
      headers['Content-Type'] = contentType ?? 'application/json'
    }
    if (this.bearerToken) {
      headers.Authorization = `Bearer ${this.bearerToken}`
    }
    if (extra) {
      Object.assign(headers, extra as Record<string, string>)
    }
    return headers
  }

  /**
   * Turn a non-OK response into the richest ApiError we can build.
   *
   * The rivulet API's error envelope is `{"error": "..."}` — that string is
   * the message a user should see (400s from the list-criteria validator are
   * meant to be surfaced verbatim). Some responses carry more than the
   * message: `409` on tag delete/merge returns the referencing lists as
   * `{"error": ..., "lists": [{id, name}]}`. The whole parsed body is kept on
   * `details` so callers can read those extra fields.
   */
  private async throwForResponse(response: Response): Promise<never> {
    // 401 on a cookie-auth request means the session has expired or was
    // never established — bounce the browser to the OIDC login endpoint
    // with the current path as return_to, behind the per-tab loop guard
    // (a blocked cookie must surface as an error, not a redirect spin).
    // Bearer-token clients get a normal ApiError so they can handle it.
    if (response.status === 401 && !this.bearerToken) {
      const redirected = redirectToLogin(currentPath())
      throw new ApiError(
        redirected ? 'Session expired' : 'Session expired; sign-in loop guard tripped',
        401,
        'no_session'
      )
    }

    const errorData = await response.json().catch(() => ({}) as Record<string, any>)

    throw new ApiError(
      errorData.error || errorData.message || response.statusText || `HTTP ${response.status}`,
      response.status,
      errorData.code,
      errorData.details ?? errorData
    )
  }

  /** Normalize thrown values into the module's error classes. */
  private normalizeError(error: unknown, timeout: number): Error {
    if (error instanceof Error && error.name === 'AbortError') {
      return new TimeoutError(`Request timeout after ${timeout}ms`)
    }
    if (error instanceof TypeError) {
      return new NetworkError('Network request failed', error)
    }
    if (error instanceof ApiError || error instanceof NetworkError || error instanceof TimeoutError) {
      return error
    }
    return new NetworkError(
      error instanceof Error ? error.message : 'Unknown error occurred',
      error instanceof Error ? error : undefined
    )
  }

  /**
   * Make an HTTP request with timeout and error handling, returning the raw
   * Response. Non-OK responses always throw.
   */
  private async send(method: string, path: string, options: SendOptions = {}): Promise<Response> {
    const { params, timeout = this.timeout, contentType, ...fetchOptions } = options

    const url = `${this.baseURL}${path}${this.buildQueryString(params)}`

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        method,
        signal: controller.signal,
        credentials: 'include',
        headers: this.buildHeaders(fetchOptions.headers, contentType),
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        await this.throwForResponse(response)
      }

      noteAuthenticated()
      return response
    } catch (error) {
      clearTimeout(timeoutId)
      throw this.normalizeError(error, timeout)
    }
  }

  /**
   * Make HTTP request and decode the JSON body.
   */
  private async request<T>(
    method: string,
    path: string,
    options: SendOptions = {}
  ): Promise<T> {
    const response = await this.send(method, path, options)

    // Handle empty responses (204 No Content, etc.)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {} as T
    }

    try {
      return (await response.json()) as T
    } catch (error) {
      throw this.normalizeError(error, 0)
    }
  }

  /**
   * HTTP GET request
   */
  async get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, options)
  }

  /**
   * HTTP POST request
   */
  async post<T>(
    path: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>('POST', path, {
      ...options,
      body: JSON.stringify(body),
    })
  }

  /**
   * HTTP PUT request
   */
  async put<T>(
    path: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>('PUT', path, {
      ...options,
      body: JSON.stringify(body),
    })
  }

  /**
   * HTTP PATCH request
   */
  async patch<T>(
    path: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>('PATCH', path, {
      ...options,
      body: JSON.stringify(body),
    })
  }

  /**
   * HTTP DELETE request
   */
  async delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, options)
  }

  /**
   * POST a body the client has already serialized (raw XML, FormData, …) and
   * decode the JSON response. Pass `contentType: null` for FormData.
   */
  async postRaw<T>(
    path: string,
    body: BodyInit,
    contentType: string | null,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>('POST', path, { ...options, body, contentType })
  }

  /**
   * GET a non-JSON payload (file downloads) as a Blob, with the server's
   * suggested filename when it sent one.
   */
  async getBlob(
    path: string,
    options?: RequestOptions
  ): Promise<{ blob: Blob; filename: string | null }> {
    const response = await this.send('GET', path, options)
    const blob = await response.blob()
    return {
      blob,
      filename: filenameFromDisposition(response.headers.get('content-disposition')),
    }
  }
}

// Create and export singleton instance
export const apiClient = new ApiClient({
  baseURL: import.meta.env.VITE_API_URL,
})

export default apiClient
